import { useManager } from 'dreamstate';
import React, { FocusEvent, LegacyRef, useCallback, useState, ReactElement } from 'react';

import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  Switch,
  rem,
} from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { BokehTooltip } from '@vimeo/live-core/components/bokeh';
import { LiveErrorBoundary } from '@vimeo/live-core/components/error/boundary';
import { useControlledPopover } from '@vimeo/live-core/hooks/dom/useControlledPopover';
import { ChatManager, IChatContext } from '@vimeo/live-core/store/firebase/interaction/chat';
import {
  trackDisableAudienceChat,
  trackEnableAudienceChat,
  trackExportChat,
} from '@vimeo/live-core/tracking/tracking_engagement_chat';

import { createInteractionDomName } from '../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../translations';
import { DownloadModal } from '../shared/DownloadModal';
import { InteractionToolsSubmenu } from '../shared/InteractionToolsSubmenu';

interface IChatSettingsButtonProps extends IBaseDomElementProps {
  withInteractionSubmenu?: boolean;
  buttonIcon: ReactElement;
  chatContext?: IChatContext;
}

export function ChatSettingsButton({
  id = createInteractionDomName('chat-settings-button'),
  className = createInteractionDomName('chat-settings-button'),
  withInteractionSubmenu = false,
  buttonIcon,
  chatContext: {
    chatSessionsHistory,
    isEnabled,
    chatActions: { enableChat, disableChat },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(ChatManager),
}: IChatSettingsButtonProps) {
  const [isDownloadOpen, setIsDownloadOpen] = useState<boolean>(false);

  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();

  const onChangeChatState = useCallback(() => {
    if (isEnabled) {
      trackDisableAudienceChat();
      disableChat();
    } else {
      trackEnableAudienceChat();
      enableChat();
    }
  }, [isEnabled, enableChat, disableChat]);

  const onOpenMenu = useCallback(() => {
    onOpenPopover();
  }, [onOpenPopover]);

  const onClickOpenDownloadModal = useCallback(() => {
    setIsDownloadOpen(true);
    onClosePopover();
  }, [onClosePopover]);

  const onClickCloseDownloadModal = useCallback(() => setIsDownloadOpen(false), []);

  const onClickDownload = useCallback(() => {
    trackExportChat();
    setIsDownloadOpen(false);
  }, []);

  const onFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <LiveErrorBoundary component={'ChatSettingsButton'} isDetailed={false}>
      <Menu
        isOpen={isOpen}
        placement={'bottom'}
        closeOnSelect={false}
        onOpen={onOpenMenu}
        onClose={onClosePopover}
      >
        <BokehTooltip label={isOpen ? null : translations.settings} placement={'bottom'}>
          <MenuButton
            as={IconButton}
            ref={triggerRef}
            id={id}
            className={className}
            aria-label={'settings'}
            icon={buttonIcon}
            size={'sm'}
            variant={'minimal'}
            onFocus={onFocus}
          />
        </BokehTooltip>

        <MenuList
          ref={contentRef as LegacyRef<HTMLDivElement>}
          overflow={'unset'}
          width={rem(240)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(4),
          }}
        >
          <MenuItem>
            <FormControl
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              width={'100%'}
            >
              <FormLabel htmlFor={'audience-chat'} marginBottom={0}>
                {translations.audienceChat}
              </FormLabel>

              <Switch
                id={'audience-chat'}
                size={'sm'}
                isChecked={Boolean(isEnabled)}
                onChange={onChangeChatState}
              />
            </FormControl>
          </MenuItem>

          <MenuItem onClick={onClickOpenDownloadModal}>{translations.download}</MenuItem>

          {withInteractionSubmenu ? (
            <>
              <InteractionToolsSubmenu
                placement={'left-start'}
                onCloseParentMenu={onClosePopover}
              />
            </>
          ) : null}
        </MenuList>
      </Menu>

      <DownloadModal
        header={translations.download}
        prefix={'export_chat'}
        history={chatSessionsHistory}
        isOpen={isDownloadOpen}
        onClose={onClickCloseDownloadModal}
        onDownload={onClickDownload}
      />
    </LiveErrorBoundary>
  );
}
